import { Checkbox, DatePicker, Input, Message, Select, Status } from '@bitcine/cinesend-theme/dist'
import useUtilities from '../../hooks/use_utilities'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import InputLocalized from './components/input_localized'
import useAuth from '../../hooks/use_auth'
import { modelTypes } from '../../constants/model_types'

const Details = ({ category, update, airline }) => {
  const [currentCategory, setCurrentCategory] = useState(category)
  const [externalUniqueNameError, setExternalUniqueNameError] = useState(null)
  const { assetTypes } = useUtilities()
  const pluralAssetTypes = assetTypes?.map((opt) => ({ ...opt, label: opt.plural_label }))
  const { user, canEditModel: canEditCategory } = useAuth(modelTypes.Category, category)

  const disabled = !canEditCategory
  // Admins can always edit; CSP editors only while no snapshot has been generated since the category was created.
  // Also requires the user to have general edit access to the category (e.g. CSPs cannot edit Sections at all).
  const canEditExternalName = canEditCategory && (user?.has_unrestricted_edit_role || !!category.external_name_editable)

  const isAdRelated = ['ad-pools', 'warning-slates'].includes(category.type)

  const adAllowedAssetTypes = ['movie', 'series']

  const assetTypesOptionsFilter = (value) => {
    if (isAdRelated) {
      return adAllowedAssetTypes.includes(value) && (airline?.ads_asset_types?.includes(value) ?? true)
    } else {
      return airline?.asset_types?.includes(value)
    }
  }

  return (
    <div className='space-y-2'>
      {category.issues.length > 0 && (
        <Message error={true} small={true} className='mb-2'>
          {category.issues[0]?.description}
        </Message>
      )}
      <div className='flex items-center space-x-4'>
        {(isAdRelated ? airline?.languages?.slice(0, 1) : airline?.languages)?.map((lang) => {
          return (
            <InputLocalized
              categoryID={category.id}
              langID={lang.id}
              key={`title-${lang.id}`}
              label={isAdRelated ? 'Title' : `${lang?.eng_description} Title`}
              defaultValue={category.category_values?.find((v) => v.language_id === lang.id)?.value}
              disabled={disabled}
            />
          )
        })}
      </div>
      {category.type !== 'warning-slates' && (
        <div>
          <Input
            label={'External Unique Name *'}
            value={currentCategory.external_unique_name ?? ''}
            error={!!externalUniqueNameError}
            disabled={!canEditExternalName}
            description={
              'A stable identifier used to match this category across cycles and systems. ' +
              'It must be unique per airline — if the same name is used for different content types (e.g. Movies, TV Series), append the content type to distinguish them. ' +
              'Only admin users can edit this field after a snapshot has been successfully generated for this cycle.'
            }
            onChange={(e) => {
              setCurrentCategory({ ...currentCategory, external_unique_name: e.target.value })
              if (externalUniqueNameError) setExternalUniqueNameError(null)
            }}
            onBlur={() => {
              if (!canEditExternalName) return
              if (!currentCategory.external_unique_name) {
                setExternalUniqueNameError('An external unique name is required.')
                return
              }
              update({ external_unique_name: currentCategory.external_unique_name }, false, {
                onError: (err) => {
                  const errors = err.response?.data?.errors
                  if (errors?.external_unique_name) {
                    setExternalUniqueNameError(
                      'This name is already in use by another category for this airline. ' +
                        'Try appending the content type (e.g. "Movies (TV Series)") to make it unique.'
                    )
                  }
                },
                onSuccess: () => setExternalUniqueNameError(null),
              })
            }}
          />
          {externalUniqueNameError && (
            <Message error={true} small={true}>
              {externalUniqueNameError}
            </Message>
          )}
        </div>
      )}

      <Select
        label={category.type !== 'warning-slates' ? 'Asset Types' : 'Asset Type'}
        placeholder='Movies, series, audiobooks, etc.'
        options={pluralAssetTypes?.filter((opt) => assetTypesOptionsFilter(opt.value))}
        isMulti={category.type !== 'warning-slates'}
        value={pluralAssetTypes?.filter((opt) => currentCategory?.asset_types?.includes(opt.value))}
        onChange={(options) => {
          const newOptions = Array.isArray(options) ? options : [options]
          update({ asset_types: newOptions.map((v) => v.value) })
          setCurrentCategory({ ...currentCategory, asset_types: newOptions.map((v) => v.value) })
        }}
        disabled={disabled}
      />
      {!currentCategory.is_root && !isAdRelated && (
        <Checkbox
          label='Enforce alphabetic sorting'
          checked={currentCategory.enforce_az_order || false}
          onChange={() => {
            update({ enforce_az_order: !currentCategory.enforce_az_order })
            setCurrentCategory({ ...currentCategory, enforce_az_order: !currentCategory.enforce_az_order })
          }}
        />
      )}

      {!category.is_root && category.type !== 'warning-slates' && (
        <>
          <DatePicker
            useLocal={false}
            label={'Start Date'}
            className='w-full'
            date={category.start_date}
            showTimeSelect={false}
            onChange={(newDate) => update({ start_date: newDate }, false)}
            disabled={disabled}
          />
          <DatePicker
            useLocal={false}
            label={'End Date'}
            className='w-full'
            date={category.end_date}
            showTimeSelect={false}
            onChange={(newDate) => update({ end_date: newDate }, false)}
            disabled={disabled}
          />
        </>
      )}
    </div>
  )
}

export default Details
